import {productTypes} from "./productHelpers";

/** @import { ProductTerm } from "./helpersTypes" */
/** @import { OptionsType } from "react-select" */
/** @import { OptionType } from "../components/Form/Select/SelectTypes" */

export const numberRegex = /[0-9]+/;

export const symbolRegex = /[-!$%^&*()_+|~=`{}[\]:";'<>?,./@#]/;

// ── Password Policy v2 ────────────────────────────────────────────────────────
// Allowed symbols per policy: ~ ! @ # $ % ^ & + = * ( ) - _ ? ' " : ; , . < > { } [ ] £ / \
export const policySymbolRegex = /[~!@#$%^&+=*()\-_?'":;,.<>{}\[\]£\/\\]/;
export const policyAllowedCharactersRegex = /^[a-zA-Z0-9~!@#$%^&+=*()\-_?'":;,.<>{}\[\]£\/\\]+$/;
export const tripleRepeatRegex = /([a-zA-Z0-9~!@#$%^&+=*()\-_?'":;,.<>{}\[\]£\/\\])\1{2,}/;
export const ascendingLettersRegex = /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i;
export const ascendingNumbersRegex = /(123|234|345|456|567|678|789)/;
export const descendingNumbersRegex = /(321|432|543|654|765|876|987)/;
export const repeatedNumberTripletsRegex = /(111|222|333|444|555|666|777|888|999|000)/;

export const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const safeLowerCase = (/** @type {string} */ str) => str ? str.toLowerCase() : '';

export const lowerCaseIncludes = (/** @type {string} */ value, /** @type {string} */ compareStr) => safeLowerCase(value).includes(safeLowerCase(compareStr));

export const lowerCaseCompare = (/** @type {string} */ str1, /** @type {string} */ str2) => safeLowerCase(str1) === safeLowerCase(str2);

/**
 * @param {string | undefined | null} str
 * @returns {str is string}
 */
export const emptyString = (str) => !str || str.trim() === '';

/** @param {string | undefined | null} email */
export const maskEmail = (email) => {
    if (!email || !email.trim()) return '';
    const atIndex = email.indexOf('@');
    if (atIndex <= 0) return '*'.repeat(email.length);
    const localPart = email.slice(0, atIndex);
    return `${localPart[0]}${'*'.repeat(Math.max(localPart.length - 1, 0))}${email.slice(atIndex)}`;
};

export const findPos = (/** @type {HTMLElement} */ obj, offset = 25) => {
    let curtop = 0;
    if (obj.offsetParent) {
        do {
            curtop += obj.offsetTop;
        // eslint-disable-next-line no-cond-assign
        } while (obj = /** @type {HTMLElement} */ (obj.offsetParent));
        return curtop - offset;
    } else {
        return obj.offsetTop - offset;
    }
};

export const integerToWord = (/** @type {number} */ integer, lowerCase = false) => {
    const numbers = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
    const word = integer < 10 ? numbers[integer] : integer.toString();
    if (lowerCase) {
        return word.toLowerCase()
    }
    return word
}

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** @type {OptionsType<OptionType>} */
export const yearOptions = Array.from({ length: 31 }, (_, i) => ({ label: `${i} Years`, value: i.toString() }));

/** @type {OptionsType<OptionType>} */
export const monthOptions = Array.from({ length: 12 }, (_, i) => ({ label: `${i} Months`, value: i.toString() }));

export const formatDate = (/** @type {Date | undefined} */ date, /** @type { { includeDay?: boolean; shortMonth?: boolean; padDay?: boolean; showTime?: boolean; weekDay?: boolean; } } */ options = {}) => {
    if (date) {
        var resultDate = new Date(date)
        let result = '';
        if (options.includeDay) {
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            result += `${days[resultDate.getDay()]}, `;
        }

        const month = options.shortMonth ? MONTHS[resultDate.getMonth()].substring(0, 3) : MONTHS[resultDate.getMonth()]
        const day = options.padDay ? resultDate.getDate().toString().padStart(2, '0') : resultDate.getDate();

        result += `${day} ${month}`;
        result += ` ${resultDate.getFullYear()}`;

        if (options.showTime) {
            result = `${result} ${resultDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            })}`
        }
        if (options.weekDay) {
            result = `${DAYS[resultDate.getDay()]} ${result}`;
        }
        return result;
    }
};

export const addDays = (/** @type {Date} */ maturityDate, /** @type {number} */ days) => {
    const date = new Date(maturityDate)
    return new Date(date.setDate(date.getDate() + days))
}

export const formatTime = (/** @type {Date | null} */ time) => {
    return time ? `${time.toLocaleTimeString([], {
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit'
    })}` : '';
};

export const joinClasses = (/** @type {(string | undefined)[]} */ ...classes) => classes.filter(x => x).join(' ');

export const classNameWithMods = (/** @type {string} */ className, /** @type {string[]} */ ...modifiers) => joinClasses(
    className,
    ...modifiers.filter(x => x).map(x => `${className}--${x}`)
);

export const formatCurrency = (/** @type {number | undefined} */ value, includeFraction = true) => {
    if (!value) {
        value = 0;
    }
    return value.toLocaleString(
        navigator.language,
        {
            style: 'currency',
            currency: 'GBP',
            currencyDisplay: 'symbol',
            useGrouping: true,
            minimumFractionDigits: includeFraction ? 2 : 0,
        }
    );
};

export const obfuscatePhoneNumber = (/** @type {string} */ mobileNumber) => {
    return `${mobileNumber.substr(0, 3)}${Array.from(mobileNumber.substr(3)).map(() => '*').join('')}`;
}

export const dateformatter = (/** @type {string | Date | undefined} */ date) => {
    if (date) {
        return formatDate(new Date(date));
    }
    return '';
}

export function canCopyToClipboard() {
    return document.queryCommandSupported('copy');
}

export function copyToClipboard(/** @type {string} */ text) {
    let successful = false;
    let textArea = document.createElement('textarea');

    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();

    let range = document.createRange();
    range.selectNodeContents(textArea);

    let s = window.getSelection();
    if (!s) {
        return successful;
    }

    s.removeAllRanges();
    s.addRange(range);

    textArea.setSelectionRange(0, 999999); // A big number, to cover anything that could be inside the element.

    try {
        successful = document.execCommand('copy');
    } catch (_err) {
        successful = false;
    }

    document.body.removeChild(textArea);

    return successful;
}

export const formatDestinationAccountType = (/** @type { null | string } */ accountType) => {

    if (!accountType)
        return "linked";
    
    if (accountType === productTypes.EasyAccess) {
        return "Online Flexi Saver"
    } else if (accountType === productTypes.Notice) {
        return "Notice Saver"
    } else if (accountType === productTypes.FixedTerm) {
        return "Fixed Rate Saver"
    }
}

export const setPageTitle = (/** @type {string} */ title) => document.title = title;

export const setMetaDescription = (/** @type {string} */ description) => {
    let metaTags = Array.from(document.getElementsByTagName('meta'));
    let descriptionTag = metaTags.filter(t => (t.getAttribute('name') || '').toLowerCase() === 'description')
    if (descriptionTag.length > 0) {
        descriptionTag[0].setAttribute('content', description);
    } else {
        let link = document.createElement('meta');
        link.setAttribute('name', 'description');
        link.setAttribute('content', description);
        document.getElementsByTagName('head')[0].appendChild(link);
    }
};

export const containsLowerCase = (/** @type {string} */ str) => str.toUpperCase() !== str;
export const containsUpperCase = (/** @type {string} */ str) => str.toLowerCase() !== str;
export const containsUpperAndLowerCase = (/** @type {string} */ str) => containsLowerCase(str) && containsUpperCase(str);
export const sortCodeRegex = /^[0-9]{6}$/;
export const accountNumberRegex = /^[0-9]{8}$/;

export const formatSortCode = (/** @type {string | undefined} */ sortCode) => {
    return sortCode && sortCode.length >= 6 ? (sortCode.replace(/-/g, '').match(/.{1,2}/g) || []).join('-') : sortCode;
};

/** 
 * @template {unknown} TElement
 * @returns {TElement[]}
 */
export const sortArrayByObjectProperty = (
    /** @type {TElement[]} */ array, 
    /** @type {string[]} */ sortByPropertyPath, 
    /** @type {(key: string) => number} */ parseFunction, 
    reverse = false
) => {
//sort an array of nested objects by any property
//requires a path to the property as a list of strings and a parseFunction used to convert the property data type into
// something that can be compared.

    const getPropertyValue = (/** @type {any} */ element, /** @type {string[]} */ path) => {
        for (let i = 0; i < path.length; i++) {
        element = element[path[i]]
        }
        return element
    }

    // deleted as the second condition is always false
    //
    // const key = parseFunction ?
    //     function (/** @type {any} */ x) {
    //         return parseFunction(getPropertyValue(x, sortByPropertyPath))
    //     } :
    //     function (/** @type {any} */ x) {
    //         return x[sortByPropertyPath]
    //     };

    const key = function (/** @type {any} */ x) {
        return parseFunction(getPropertyValue(x, sortByPropertyPath))
    };

    const shouldReverse = !reverse ? 1 : -1;

    var sortOrder = function (/** @type {TElement} */ a, /** @type {TElement} */ b) {
        // @ts-expect-error the code below is upsetting TS
        return a = key(a), b = key(b), shouldReverse * ((a > b) - (b > a));
    }
    return array.sort(sortOrder)
}

export const convertProductTermToMonths = (/** @type {ProductTerm} */ productTerm) => {
    const daysInMonth = 30.44; // average across the year
    switch (productTerm.unit.toLowerCase()) {
        case "day":
            return productTerm.value * (1 / daysInMonth);
        case "week":
            return productTerm.value * (daysInMonth / 7);
        case "month":
            return productTerm.value;
        default:
            throw new Error('Invalid unit. Supported unit values are "day", "week" or "month".');
    }
}

export function formatProductTerm(/** @type {ProductTerm | undefined} */ productTerm, pluralise = true) {
    if (!productTerm) {
        return '';
    }

    let quantity = productTerm.value;
    let unit = productTerm.unit.toLowerCase();
    
    if (unit === "month") {
        const years = (quantity / 12);
        if (years % 1 === 0) {
            quantity = years;
            unit = "year";
        }
    }
    
    if (pluralise) {
        return `${quantity} ${quantity > 1 ? unit + 's' : unit}`;
    }
    
    return `${quantity} ${unit}`;
}

export const existingApplicationAcceptedInSession = (/** @type {import('./helpersTypes').ProductType[]} */ products) => {
    const productType = products[0].productType;

    // Only check for Easy Access due to product rules
    if (productType !== productTypes.EasyAccess)
        return false;

    return products.filter(p => sessionStorage.getItem(`app-accepted-${p.productCode}`)).length > 0;
}
